import { Component } from '@theme/component';
import { debounce, lockScroll, unlockScroll } from '@theme/utilities';
import { createNostoProductCard, hydrateNostoCardsFromShopify } from '@theme/bt-nosto-product-card';
import {
  bindNostoSearchTracking,
  queryNosto,
  recordNostoSearchSubmit,
  unwrapSearchResponse,
  waitForNostojs,
  writeNostoSearchCache,
} from '@theme/bt-nosto-search';

const INPUT_SELECTOR = '[data-bt-search] [data-searchbar-input]';
const TRIGGER_SELECTOR = '[data-header-search-trigger]';
const SEARCH_DEBOUNCE_MS = 300;
const CARD_BATCH_LIMIT = 20;

/**
 * @typedef {import('@theme/bt-nosto-search').NostoProductHit} NostoProductHit
 * @typedef {import('@theme/bt-nosto-search').NostoSearchResponse} NostoSearchResponse
 */

/**
 * @typedef {object} Refs
 * @property {HTMLElement} panel
 * @property {HTMLButtonElement} close
 * @property {HTMLElement} sorry
 * @property {HTMLElement} popular
 * @property {HTMLElement} suggestions
 * @property {HTMLElement} suggestionList
 * @property {HTMLElement} heading
 * @property {HTMLAnchorElement} viewAll
 * @property {HTMLElement} trending
 * @property {HTMLElement} results
 */

/**
 * Bo+Tee Nosto search overlay (Figma Search Open / Search Active).
 * @extends {Component<Refs>}
 */
class BtSearchOverlay extends Component {
  requiredRefs = [
    'panel',
    'close',
    'sorry',
    'popular',
    'suggestions',
    'suggestionList',
    'heading',
    'viewAll',
    'trending',
    'results',
  ];

  /** @type {AbortController} */
  #listeners = new AbortController();

  /** @type {number} */
  #searchSeq = 0;

  /** @type {boolean} */
  #open = false;

  /** @type {HTMLInputElement | null} */
  #activeInput = null;

  /** @type {string} */
  #trendingHeading = '';

  /** @type {string} */
  #trendingUrl = '';

  /** @type {ReturnType<typeof bindNostoSearchTracking> | null} */
  #nostoTracking = null;

  /** @type {Map<string, DocumentFragment>} */
  #cardCache = new Map();

  connectedCallback() {
    super.connectedCallback();

    this.#trendingHeading = this.refs.heading.textContent?.trim() ?? '';
    this.#trendingUrl = this.dataset.trendingUrl || this.#searchUrl();
    this.#nostoTracking = bindNostoSearchTracking(this.refs.results, 'autocomplete');

    const { signal } = this.#listeners;

    this.refs.close.addEventListener('click', this.close, { signal });
    this.refs.popular.addEventListener('click', this.#onTermClick, { signal });
    this.refs.suggestions.addEventListener('click', this.#onSuggestionClick, { signal });
    this.refs.viewAll.addEventListener('click', this.#onViewAllClick, { signal });

    document.addEventListener('click', this.#onDocumentClick, { signal });
    document.addEventListener('focusin', this.#onFocusIn, { signal });
    document.addEventListener('input', this.#onDocumentInput, { capture: true, signal });
    document.addEventListener('submit', this.#onDocumentSubmit, { signal });
    document.addEventListener('keydown', this.#onDocumentKeydown, { signal });
    window.addEventListener('resize', this.#syncOffset, { signal });
    window.visualViewport?.addEventListener('resize', this.#syncOffset, { signal });
    window.visualViewport?.addEventListener('scroll', this.#syncOffset, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#listeners.abort();
    this.#nostoTracking?.disconnect();
    this.#nostoTracking = null;
    this.#headerResize.disconnect();
    this.#abortSearch();
    this.#debouncedSearch.cancel();
    if (this.#open) unlockScroll(this);
  }

  /**
   * @param {HTMLInputElement | null} [input]
   */
  open = (input) => {
    const drawerInput = input?.closest?.('[data-bt-nav-drawer]') ? input : null;
    if (drawerInput) this.#closeNavDrawer();

    if (this.#open) {
      this.#syncHeader();
      this.#syncOffset();
      requestAnimationFrame(this.#syncOffset);
      this.#adoptHeaderInput(drawerInput);
      return;
    }

    this.#open = true;
    this.hidden = false;
    this.classList.add('is-open');
    document.documentElement.classList.add('bt-search-open');
    lockScroll(this);
    this.#closeNavDrawer();
    this.#syncHeader();
    const header = document.getElementById('header-component');
    if (header instanceof HTMLElement) this.#headerResize.observe(header);
    this.#syncOffset();
    requestAnimationFrame(() => {
      this.#syncOffset();
      requestAnimationFrame(this.#syncOffset);
    });

    this.#adoptHeaderInput(drawerInput ?? input);

    const query = this.#activeInput?.value?.trim() ?? '';
    if (query) {
      this.#debouncedSearch(query);
    } else {
      this.#showEmpty();
    }

    this.dispatchEvent(new CustomEvent('bt-search:open', { bubbles: true }));
  };

  close = () => {
    if (!this.#open) return;

    this.#open = false;
    this.#searchSeq += 1;
    this.#abortSearch();
    this.#debouncedSearch.cancel();
    this.classList.remove('is-open');
    this.hidden = true;
    document.documentElement.classList.remove('bt-search-open');
    unlockScroll(this);
    this.#syncHeader(false);
    this.#headerResize.disconnect();
    this.style.removeProperty('--bt-search-overlay-top');
    this.style.removeProperty('--bt-search-overlay-height');
    this.dispatchEvent(new CustomEvent('bt-search:close', { bubbles: true }));
  };

  /**
   * @param {MouseEvent} event
   */
  #onDocumentClick = (event) => {
    const target = /** @type {HTMLElement | null} */ (event.target instanceof Element ? event.target : null);
    const trigger = target?.closest(TRIGGER_SELECTOR);
    if (trigger instanceof HTMLElement) {
      event.preventDefault();
      if (this.#open) {
        this.close();
        return;
      }

      this.open(this.#visibleInput());
      this.#visibleInput()?.focus();
      return;
    }

    const search = target?.closest('[data-bt-search]');
    if (!(search instanceof HTMLElement)) return;
    if (this.#open && !search.closest('[data-bt-nav-drawer]')) return;

    const input = search.querySelector('[data-searchbar-input]');
    this.open(input instanceof HTMLInputElement ? input : null);
  };

  /**
   * @param {FocusEvent} event
   */
  #onFocusIn = (event) => {
    const input = this.#searchInputFromEvent(event);
    if (!input) return;
    this.open(input);
  };

  /**
   * @param {Event} event
   */
  #onDocumentInput = (event) => {
    const input = this.#searchInputFromEvent(event);
    if (!input) return;

    this.#syncInputs(input.value);
    this.open(input);

    const query = input.value.trim();
    if (!query) {
      this.#searchSeq += 1;
      this.#abortSearch();
      this.#debouncedSearch.cancel();
      this.#showEmpty();
      return;
    }

    this.#debouncedSearch(query);
  };

  /**
   * @param {SubmitEvent} event
   */
  #onDocumentSubmit = (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    if (!form.closest('[data-bt-search]')) return;

    const input = form.querySelector('[data-searchbar-input]');
    const query = input instanceof HTMLInputElement ? input.value.trim() : '';
    this.#closeNavDrawer();
    if (!query) {
      event.preventDefault();
      this.open(input instanceof HTMLInputElement ? input : null);
      return;
    }

    event.preventDefault();
    this.#goToSerp(query, { isKeyword: false, recordSubmit: true });
  };

  /**
   * @param {KeyboardEvent} event
   */
  #onDocumentKeydown = (event) => {
    if (event.key === 'Escape' && this.#open) {
      event.preventDefault();
      this.close();
      this.#visibleInput()?.blur();
    }
  };

  /**
   * @param {MouseEvent} event
   */
  #onTermClick = (event) => {
    const button = event.target instanceof Element ? event.target.closest('[data-search-term]') : null;
    if (!(button instanceof HTMLElement)) return;
    const term = button.dataset.searchTerm?.trim();
    if (!term) return;
    this.#applyQuery(term);
  };

  /**
   * @param {MouseEvent} event
   */
  #onSuggestionClick = (event) => {
    const button = event.target instanceof Element ? event.target.closest('[data-search-term]') : null;
    if (!(button instanceof HTMLElement)) return;
    const term = button.dataset.searchTerm?.trim();
    if (!term) return;

    event.preventDefault();
    const url = button.dataset.searchUrl;
    if (url) {
      this.close();
      window.location.assign(url);
      return;
    }

    this.#syncInputs(term);
    this.#goToSerp(term, { isKeyword: true, recordSubmit: true });
  };

  /**
   * @param {string} term
   */
  #applyQuery = (term) => {
    this.#syncInputs(term);
    const input = this.#visibleInput();
    input?.focus();
    this.open(input);
    this.#debouncedSearch.cancel();
    this.#runSearch(term);
  };

  #debouncedSearch = debounce((/** @type {string} */ query) => {
    this.#runSearch(query);
  }, SEARCH_DEBOUNCE_MS);

  /** @type {AbortController | null} */
  #searchAbort = null;

  #abortSearch() {
    this.#searchAbort?.abort();
    this.#searchAbort = null;
  }

  /**
   * @param {string} query
   */
  async #runSearch(query) {
    const seq = ++this.#searchSeq;
    this.#abortSearch();
    const abort = new AbortController();
    this.#searchAbort = abort;

    try {
      const response = await this.#queryNosto(query, abort.signal);
      if (seq !== this.#searchSeq) return;
      await this.#renderLive(query, response, seq);
    } catch (error) {
      if (seq !== this.#searchSeq || abort.signal.aborted) return;
      console.warn('[bt-search-overlay] Nosto search failed', error);
      this.#showNone(query);
    }
  }

  /**
   * @param {string} query
   * @param {AbortSignal} signal
   * @returns {Promise<NostoSearchResponse>}
   */
  #queryNosto(query, signal) {
    return queryNosto(query, signal);
  }

  /**
   * @param {string} query
   * @param {NostoSearchResponse} response
   * @param {number} seq
   */
  async #renderLive(query, response, seq) {
    const search = unwrapSearchResponse(response);
    const products = search.products?.hits ?? [];
    const total = Number(search.products?.total ?? products.length);

    if (total === 0 && products.length === 0) {
      this.#showNone(query);
      return;
    }

    writeNostoSearchCache(query, search);
    const suggestions = this.#suggestionItems(search);
    this.#setLiveChrome(query, total, suggestions);
    await this.#renderProductHits(products, seq);
    if (seq !== this.#searchSeq) return;
    this.refs.panel.scrollTop = 0;
  }

  /**
   * @param {NostoSearchResponse} response
   * @returns {Array<{ label: string, term: string, url?: string }>}
   */
  #suggestionItems(response) {
    const keywords = (response.keywords?.hits ?? [])
      .map((hit) => hit.keyword?.trim())
      .filter(Boolean)
      .map((keyword) => ({ label: /** @type {string} */ (keyword), term: /** @type {string} */ (keyword) }));

    const categories = (response.categories?.hits ?? []).flatMap((hit) => {
      const label = hit.name?.trim();
      const url = this.#categoryUrl(hit);
      return label && url ? [{ label, term: label, url }] : [];
    });

    return [...keywords, ...categories];
  }

  /**
   * Nosto indexes the production domain; keep navigation on the current storefront.
   * @param {{ url?: string, urlPath?: string }} hit
   * @returns {string | undefined}
   */
  #categoryUrl(hit) {
    if (hit.url) {
      try {
        return new URL(hit.url, window.location.origin).pathname;
      } catch {
        // fall through to urlPath
      }
    }
    const handle = hit.urlPath?.replace(/^\/+|\/+$/g, '');
    if (!handle) return undefined;
    return handle.includes('/') ? `/${handle}` : `/collections/${handle}`;
  }

  /**
   * @param {string} query
   * @param {number} total
   * @param {Array<{ label: string, term: string, url?: string }>} suggestions
   */
  #setLiveChrome(query, total, suggestions) {
    this.refs.sorry.hidden = true;
    const hasSuggestions = suggestions.length > 0;
    this.refs.popular.hidden = hasSuggestions;
    this.refs.suggestions.hidden = !hasSuggestions;
    this.refs.trending.hidden = true;
    this.refs.results.hidden = false;

    this.refs.heading.textContent = this.#resultsHeading(total);
    this.refs.viewAll.href = this.#serpUrl(query);

    this.refs.suggestionList.replaceChildren(
      ...suggestions.map((item) => {
        const li = document.createElement('li');
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'bt-search-overlay__term text-p4 color-text-subdued';
        button.dataset.searchTerm = item.term;
        if (item.url) button.dataset.searchUrl = item.url;
        button.textContent = item.label;
        li.append(button);
        return li;
      })
    );
  }

  /**
   * @param {NostoProductHit[]} hits
   * @param {number} seq
   */
  async #renderProductHits(hits, seq) {
    const wraps = hits.map((hit, index) => this.#nostoCardWrap(hit, index));
    const missingIndexes = wraps.flatMap((wrap, index) => (wrap ? [] : [index]));

    if (missingIndexes.length) {
      const missingHandles = missingIndexes
        .map((index) => handleFromProductUrl(hits[index]?.url))
        .filter(Boolean);
      await this.#prefetchCards(missingHandles);
      if (seq !== this.#searchSeq) return;

      const fallbacks = await Promise.all(
        missingIndexes.map((index) => this.#liquidCardWrap(hits[index]))
      );
      if (seq !== this.#searchSeq) return;
      missingIndexes.forEach((itemIndex, fallbackIndex) => {
        wraps[itemIndex] = fallbacks[fallbackIndex];
      });
    }

    const rendered = wraps.filter(Boolean);
    this.refs.results.replaceChildren(...rendered);
    void hydrateNostoCardsFromShopify(
      rendered.flatMap((wrap) => [...wrap.querySelectorAll('product-card[id^="product-card-nosto-"]')])
    );
  }

  /**
   * @param {NostoProductHit} hit
   * @param {number} index
   * @returns {HTMLElement | null}
   */
  #nostoCardWrap(hit, index) {
    try {
      const card = createNostoProductCard(hit, { host: this, index });
      return card ? this.#wrapCard(card) : null;
    } catch (error) {
      console.warn('[bt-search-overlay] Nosto card render failed, using Liquid fallback', error);
      return null;
    }
  }

  /**
   * @param {NostoProductHit | undefined} hit
   * @returns {Promise<HTMLElement | null>}
   */
  async #liquidCardWrap(hit) {
    const handle = handleFromProductUrl(hit?.url);
    if (!handle) return null;
    const card = await this.#fetchCard(handle);
    return card ? this.#wrapCard(card) : null;
  }

  /**
   * @param {Element} card
   * @returns {HTMLElement}
   */
  #wrapCard(card) {
    const wrap = document.createElement('div');
    wrap.className = 'bt-search-overlay__grid-item';
    wrap.append(card);
    return wrap;
  }

  /**
   * @param {string[]} handles
   */
  async #prefetchCards(handles) {
    const missing = [...new Set(handles)].filter((handle) => !this.#cardCache.has(handle));
    if (!missing.length) return;

    const batches = [];
    for (let index = 0; index < missing.length; index += CARD_BATCH_LIMIT) {
      batches.push(missing.slice(index, index + CARD_BATCH_LIMIT));
    }

    const results = await Promise.allSettled(batches.map((batch) => this.#fetchCardBatch(batch)));
    for (const result of results) {
      if (result.status === 'rejected') {
        console.warn('[bt-search-overlay] Batch card hydration failed, using per-product fallback', result.reason);
      }
    }
  }

  /**
   * @param {string[]} handles
   */
  async #fetchCardBatch(handles) {
    const root = window.Shopify?.routes?.root || '/';
    const url = `${root}search?q=${encodeURIComponent(handles.join(','))}&type=product&view=bt-product-cards`;
    const response = await fetch(url);
    if (!response.ok) throw new Error(`Card batch failed: ${response.status}`);

    const html = await response.text();
    const doc = new DOMParser().parseFromString(html, 'text/html');
    for (const wrapper of doc.querySelectorAll('[data-product-handle]')) {
      if (!(wrapper instanceof HTMLElement)) continue;
      const handle = wrapper.dataset.productHandle;
      const card = wrapper.querySelector('product-card');
      if (!handle || !card) continue;
      this.#storeCard(handle, card);
    }
  }

  /**
   * @param {string} handle
   * @returns {Promise<Element | null>}
   */
  async #fetchCard(handle) {
    const cached = this.#cardCache.get(handle);
    if (cached) return /** @type {Element} */ (cached.firstElementChild?.cloneNode(true) ?? null);

    const root = window.Shopify?.routes?.root || '/';
    const url = `${root}products/${encodeURIComponent(handle)}?view=card`;
    const response = await fetch(url);
    if (!response.ok) return null;

    const html = await response.text();
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const card = doc.querySelector('product-card');
    if (!card) return null;

    this.#storeCard(handle, card);
    return card;
  }

  /**
   * @param {string} handle
   * @param {Element} card
   */
  #storeCard(handle, card) {
    const fragment = document.createDocumentFragment();
    fragment.append(card.cloneNode(true));
    this.#cardCache.set(handle, fragment);
  }

  #showEmpty() {
    this.refs.sorry.hidden = true;
    this.refs.popular.hidden = false;
    this.refs.suggestions.hidden = true;
    this.refs.trending.hidden = false;
    this.refs.results.hidden = true;
    this.refs.heading.textContent = this.#trendingHeading;
    this.refs.viewAll.href = this.#trendingUrl;
    this.refs.results.replaceChildren();
    this.refs.suggestionList.replaceChildren();
    this.refs.panel.scrollTop = 0;
  }

  /**
   * @param {string} [query]
   */
  #showNone(query = '') {
    if (query) writeNostoSearchCache(query, { products: { total: 0, hits: [] } });
    const template = this.dataset.sorryTemplate || 'sorry, no results for ‘__QUERY__’';
    this.refs.sorry.textContent = template.replace('__QUERY__', query);
    this.refs.sorry.hidden = false;
    this.refs.popular.hidden = false;
    this.refs.suggestions.hidden = true;
    this.refs.trending.hidden = false;
    this.refs.results.hidden = true;
    this.refs.heading.textContent = this.#trendingHeading;
    this.refs.viewAll.href = this.#trendingUrl;
    this.refs.results.replaceChildren();
    this.refs.suggestionList.replaceChildren();
    this.refs.panel.scrollTop = 0;
  }

  /**
   * @param {MouseEvent} event
   */
  #onViewAllClick = (event) => {
    const srp = document.querySelector('bt-search-results');
    if (!srp || typeof srp.search !== 'function') return;

    const query = this.#visibleInput()?.value?.trim() ?? '';
    if (!query) return;

    event.preventDefault();
    this.#goToSerp(query, { isKeyword: false, recordSubmit: true });
  };

  /**
   * @param {string} query
   * @param {{ isKeyword?: boolean, recordSubmit?: boolean }} [options]
   */
  #goToSerp(query, options = {}) {
    this.#closeNavDrawer();
    if (options.recordSubmit) recordNostoSearchSubmit('autocomplete', query);
    this.#withNosto((api) => {
      if (options.isKeyword) {
        api.search?.({ query }, { track: 'serp', isKeyword: true }).catch(() => {});
      }
    });

    const srp = document.querySelector('bt-search-results');
    if (srp && typeof srp.search === 'function') {
      this.close();
      srp.search(query);
      return;
    }

    window.location.assign(this.#serpUrl(query));
  }

  /**
   * @param {(api: any) => void} callback
   */
  #withNosto(callback) {
    waitForNostojs().then((nostojs) => {
      if (!nostojs) return;
      nostojs(callback);
    });
  }

  /**
   * @param {string} query
   */
  #serpUrl(query) {
    const url = new URL(this.#searchUrl(), window.location.origin);
    url.searchParams.set('q', query);
    return `${url.pathname}${url.search}`;
  }

  #searchUrl() {
    return this.dataset.searchUrl || window.Theme?.routes?.search_url || '/search';
  }

  /**
   * @param {number} total
   */
  #resultsHeading(total) {
    const template = this.dataset.resultsTemplate || '__count__ results';
    return template.replace('__count__', String(total));
  }

  /**
   * @param {string} value
   */
  #syncInputs(value) {
    document.querySelectorAll(INPUT_SELECTOR).forEach((node) => {
      if (!(node instanceof HTMLInputElement)) return;
      if (node.value !== value) node.value = value;
      const bar = node.closest('search-bar');
      if (bar && typeof bar.syncFromInput === 'function') bar.syncFromInput();
    });
  }

  /** @type {ResizeObserver} */
  #headerResize = new ResizeObserver(() => {
    this.#syncOffset();
  });

  #syncOffset = () => {
    if (!this.#open) return;
    const header = document.getElementById('header-component');
    if (!(header instanceof HTMLElement)) return;

    const vv = window.visualViewport;
    const visualOffset = vv?.offsetTop ?? 0;
    const row = header.querySelector('.header__search-row');
    const box =
      row instanceof HTMLElement && row.offsetParent !== null && row.getClientRects().length > 0
        ? row
        : header;
    const rect = box.getBoundingClientRect();

    // Visual-viewport relative. Sit flush under the header field; 1px overlap
    // hides the hairline. Do not add --header-sticky-top on top of the rect
    // (that left a gap the page search bar showed through).
    const top = Math.max(0, Math.round(rect.bottom - visualOffset) - 1);
    this.style.setProperty('--bt-search-overlay-top', `${top}px`);

    if (vv) {
      const height = Math.max(0, Math.round(vv.height - top));
      this.style.setProperty('--bt-search-overlay-height', `${height}px`);
    } else {
      this.style.removeProperty('--bt-search-overlay-height');
    }
  };

  /**
   * @param {boolean} [open]
   */
  #syncHeader(open = this.#open) {
    const header = document.getElementById('header-component');
    if (header instanceof HTMLElement) {
      if (open) header.setAttribute('data-search-open', 'true');
      else header.removeAttribute('data-search-open');
    }
    document.querySelectorAll(TRIGGER_SELECTOR).forEach((trigger) => {
      trigger.setAttribute('aria-expanded', open ? 'true' : 'false');
    });
  }

  #visibleInput() {
    const inputs = [...document.querySelectorAll(INPUT_SELECTOR)].filter((node) => {
      if (!(node instanceof HTMLInputElement)) return false;
      if (node.closest('[data-bt-nav-drawer]')) return false;
      return node.offsetParent !== null;
    });
    return /** @type {HTMLInputElement | undefined} */ (inputs[0]);
  }

  /**
   * Drawer search is only a trigger — type in the header field once the overlay is open.
   * @param {HTMLInputElement | null} [source]
   * @param {boolean} [retried]
   */
  #adoptHeaderInput(source, retried = false) {
    const headerInput = this.#visibleInput();
    if (source && headerInput && source !== headerInput && source.value !== headerInput.value) {
      headerInput.value = source.value;
      const bar = headerInput.closest('search-bar');
      if (bar && typeof bar.syncFromInput === 'function') bar.syncFromInput();
    }

    const next = headerInput ?? (source?.closest('[data-bt-nav-drawer]') ? null : source) ?? null;
    if (next) this.#activeInput = next;
    if (next && document.activeElement !== next) next.focus();

    if (!headerInput && !retried && source?.closest('[data-bt-nav-drawer]')) {
      requestAnimationFrame(() => this.#adoptHeaderInput(source, true));
    }
  }

  /**
   * @param {Event} event
   * @returns {HTMLInputElement | null}
   */
  #searchInputFromEvent(event) {
    const target = event.target;
    if (!(target instanceof HTMLInputElement)) return null;
    if (!target.matches('[data-searchbar-input]')) return null;
    if (!target.closest('[data-bt-search]')) return null;
    return target;
  }

  #closeNavDrawer() {
    const drawer = document.querySelector('header-drawer');
    if (drawer && 'close' in drawer && typeof drawer.close === 'function') {
      drawer.close({ restoreFocus: false });
    }
  }
}

/**
 * @param {string} [url]
 * @returns {string}
 */
function handleFromProductUrl(url) {
  if (!url) return '';
  try {
    const path = new URL(url, window.location.origin).pathname;
    const match = path.match(/\/products\/([^/]+)/);
    return match ? decodeURIComponent(match[1]) : '';
  } catch {
    return '';
  }
}

if (!customElements.get('bt-search-overlay')) {
  customElements.define('bt-search-overlay', BtSearchOverlay);
}

waitForNostojs();
